import { Dropdown, HStack, Spacer } from '@sgme/ui';
import clsx from 'clsx';
import { isValid, parseISO } from 'date-fns';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import { Fragment } from 'react/jsx-runtime';

import { toMultipassLegId } from '@/api/option/utils.ts';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useQueuedDispatch } from '@/App/tiles/Option/QueueContext.tsx';
import { GroupedPricingSummary } from '@/App/tiles/Option/strategy/GroupedPricingSummary/GroupedPricingSummary.tsx';
import { SharedHedgeFields } from '@/App/tiles/Option/strategy/SharedHedgeFields.tsx';
import type { StrategyExpiryGroup } from '@/App/tiles/Option/strategy/strategyGrouping.ts';
import { isHedgeField } from '@/App/tiles/Option/strategy/strategyLayout.ts';
import { StrategyLegFields } from '@/App/tiles/Option/strategy/StrategyLegFields.tsx';
import type { StrategyFields, StrategyOrientation } from '@/App/tiles/Option/strategy/types.ts';
import { FormattedFXDate } from '@/components/ui/FormattedFXDate.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import type { OptionProductName, OptionQuote, LegId } from '@/store/option/state.ts';
import {
  changeOptionTypeThunk,
  resetOptionThunk,
  duplicateOptionLegThunk,
  removeOptionLegThunk,
} from '@/store/option/thunks/thunk.ts';

// Type definition for the props of the StrategyGroup component
type StrategySectionProps = {
  tileId: string;
  group: StrategyExpiryGroup;
  fields: StrategyFields;
  mode: StrategyOrientation;
  groupedByExpiry: boolean;
  productName: OptionProductName;
  quote: OptionQuote | undefined;
  isTriggerOrBinary: boolean;
  onToggle: () => void;
};

export function StrategySection({
  tileId,
  group,
  fields,
  mode,
  groupedByExpiry,
  productName,
  quote,
  isTriggerOrBinary,
  onToggle,
}: StrategySectionProps) {
  const { expiryDate, legs, isOpen, canGroupHedges, groupColumn, columnSpan } = group;
  const isVerticalGroup = groupedByExpiry && mode === 'vertical';
  const hasGroupHedges = fields.flat().some(isHedgeField);

  // Place totals and shared editors together, once, in the header subgrid or vertical grid.
  const groupedFields = groupedByExpiry && (
    <>
      <GroupedPricingSummary
        legs={legs}
        fields={fields}
        groupColumn={groupColumn}
        isOpen={isOpen}
        mode={mode}
        canGroupHedges={canGroupHedges}
      />

      {canGroupHedges && (
        <SharedHedgeFields
          tileId={tileId}
          legs={legs}
          quoteLeg={legs[0] === undefined ? undefined : quote?.legsData[toMultipassLegId(legs[0].legId, productName)]}
          fields={fields}
          groupColumn={groupColumn}
          isOpen={isOpen}
          mode={mode}
        />
      )}
    </>
  );

  return (
    <>
      {groupedByExpiry && (
        <div
          style={isVerticalGroup ? { gridColumn: `${groupColumn} / span ${columnSpan}` } : undefined}
          className={clsx('group-header ps-1', isOpen ? 'open' : 'closed', {
            summary: quote !== undefined || (hasGroupHedges && canGroupHedges),
          })}
        >
          <button type="button" className="group-header-label" onClick={onToggle} disabled={legs.length === 1}>
            <FormatDateString date={expiryDate} /> ({legs.length} legs){' '}
            {legs.length > 1 && (
              <em className="icon icon-sm cursor-pointer">{isOpen ? 'keyboard_arrow_left' : 'keyboard_arrow_right'}</em>
            )}
          </button>
          {mode === 'horizontal' && groupedFields}
        </div>
      )}

      {isVerticalGroup && groupedFields}

      {legs.map((leg, legIndex) => (
        <Fragment key={leg.legId}>
          <LegHeader
            tileId={tileId}
            legId={leg.legId}
            visible={isOpen}
            isTriggerOrBinary={isTriggerOrBinary}
            style={isVerticalGroup ? { gridColumn: groupColumn + legIndex, gridRow: 2 } : undefined}
          />

          {isOpen && (
            <StrategyLegFields
              tileId={tileId}
              fields={fields}
              mode={mode}
              leg={leg}
              quoteLeg={quote?.legsData?.[toMultipassLegId(leg.legId, productName)]}
              column={isVerticalGroup ? groupColumn + legIndex : undefined}
              sharedHedges={groupedByExpiry && canGroupHedges}
            />
          )}
        </Fragment>
      ))}
    </>
  );
}

function LegHeader({
  tileId,
  visible,
  legId,
  isTriggerOrBinary,
  style,
}: {
  tileId: string;
  legId: LegId;
  visible: boolean;
  isTriggerOrBinary: boolean;
  style?: CSSProperties;
}) {
  const queuedDispatch = useQueuedDispatch();
  const {
    currentUser: {
      featureToggles: { optionBinary, optionTrigger },
    },
  } = useUserContext();

  const { currencyPair, legs, productName, uiState } = useOptionSelector((option) => ({
    legs: option.properties.legs ?? [],
    productName: option.type,
    currencyPair: selectOptionCurrency(option).currencyPair,
    uiState: option.properties.uiState,
  }));

  const isStrategyProduct = productName === 'Strangle' || productName === 'Straddle' || productName === 'RiskReversal';

  const options = (
    ['Vanilla', 'Trigger', 'Binary', 'Straddle', 'Strangle', 'RiskReversal'] as OptionProductName[]
  ).filter(
    (type) =>
      (type !== 'Trigger' || optionTrigger) &&
      (type !== 'Binary' || optionBinary) &&
      (isStrategyProduct || legs.length < 2 || (type !== 'Straddle' && type !== 'Strangle' && type !== 'RiskReversal')),
  );

  async function changeOptionType(changedProductName: OptionProductName) {
    const shouldResetOption =
      isStrategyProduct ||
      changedProductName === 'Strangle' ||
      changedProductName === 'Straddle' ||
      changedProductName === 'RiskReversal';

    if (!shouldResetOption) {
      await queuedDispatch(changeOptionTypeThunk({ tileId, legProductName: changedProductName, legId }));
      return;
    }

    await queuedDispatch(
      resetOptionThunk({
        tileId: tileId,
        newType: changedProductName,
        currencyPair,
        uiState,
      }),
    );
  }

  async function handleDuplicateLeg() {
    await queuedDispatch(duplicateOptionLegThunk({ tileId, legId }));
  }

  async function handleRemoveLeg() {
    await queuedDispatch(removeOptionLegThunk({ tileId, legId }));
  }

  return (
    <HStack className={clsx('grid-cell', { 'd-none': !visible })} style={style}>
      <Dropdown color={'flat-primary' as 'primary'} direction="auto" size="md">
        <Dropdown.Toggle data-e2e="strategy-leg-label">
          {productName === 'RiskReversal' || productName === 'Straddle' || productName === 'Strangle'
            ? productName
            : legs.find((leg) => leg.legId === legId)?.legProductName}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {options.map((productName) => (
            <Dropdown.Item
              key={productName}
              onClick={() => changeOptionType(productName)}
              data-e2e={`select-${productName}`}
            >
              <FormattedMessage id={`option.type.${productName}`} />
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {(productName === 'Vanilla' || isTriggerOrBinary) && (
        <>
          <Spacer />
          <Dropdown color={'flat-primary' as 'primary'} direction="auto" size="md">
            <Dropdown.Toggle className="btn-icon" data-e2e="legMenu-btn">
              <i className="icon">more_vert</i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleDuplicateLeg} data-e2e="duplicateLeg-menu-item">
                <FormattedMessage id="option.actions.duplicateLeg" />
              </Dropdown.Item>
              {legs.length > 1 && (
                <Dropdown.Item
                  onClick={handleRemoveLeg}
                  // state={canRemoveLeg ? undefined : 'disabled'}
                  data-e2e="removeLeg-menu-item"
                >
                  <FormattedMessage id="option.actions.removeLeg" />
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </>
      )}
    </HStack>
  );
}

function FormatDateString({ date }: { date: string }) {
  return <>{isValid(parseISO(date)) ? <FormattedFXDate value={date} dateStyle="medium" /> : 'No date'}</>;
}
